import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import { NavLink } from 'react-router-dom'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../../hooks/useAuth'

import {
    CardContent,
    Grid,
    LinearProgress,
    Link,
    Card as MuiCard,
    Divider as MuiDivider,
    Typography,
} from '@mui/material'

import { spacing } from '@mui/system'
import InfoBlock from '../components/InfoBlock'
import { formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import AdvisorApi from '../../api/AdvisorApi'
import IssueApi from '../../api/IssueApi'

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

function DetailsView({ entity }) {
    return (
        <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 3 }}>
                <PlaceHolder entity={entity} />
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
                <MainInfo entity={entity} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <IssuesDetails entity={entity} />
            </Grid>
        </Grid>
    )
}

function MainInfo({ entity }) {
    let content = null
    if (!entity) {
        content = <LinearProgress />
    } else {
        content = (
            <React.Fragment>
                <Typography variant='body2' gutterBottom>
                    {entity.name} .
                </Typography>
            </React.Fragment>
        )
    }
    return (
        <Card mb={6}>
            <CardContent>
                <Typography variant='h6' gutterBottom>
                    Allmänt om {entity?.name || 'rådgivare'}
                </Typography>
                {content}
            </CardContent>
        </Card>
    )
}

function PlaceHolder() {
    return <InfoBlock title='Bolag' value='Vad ska vi skriva här?' />
}

const advisorSummaryText = (normalizedIssues) => {
    const numTotalIssues = Object.keys(normalizedIssues).length
    const numAsFinancialAdvisor = Object.values(normalizedIssues).filter(
        (i) => i.advisorType === 'financial_advisor',
    ).length
    const numAsLegalAdvisor = Object.values(normalizedIssues).filter((i) => i.advisorType === 'legal_advisor').length
    const numAsIssuingAgent = Object.values(normalizedIssues).filter((i) => i.advisorType === 'issuing_agent').length
    if (numAsFinancialAdvisor > 0 && numAsLegalAdvisor === 0 && numAsIssuingAgent === 0) {
        return `Rådgivare i ${numAsFinancialAdvisor} emissioner som finansiell rådgivare.`
    }
    if (numAsFinancialAdvisor === 0 && numAsLegalAdvisor > 0 && numAsIssuingAgent === 0) {
        return `Rådgivare i ${numAsLegalAdvisor} emissioner som juridisk rådgivare.`
    }
    if (numAsFinancialAdvisor === 0 && numAsLegalAdvisor === 0 && numAsIssuingAgent > 0) {
        return `Rådgivare i ${numAsIssuingAgent} emissioner som emissionsinstitut.`
    }
    if (numAsFinancialAdvisor > 0 && numAsLegalAdvisor > 0 && numAsIssuingAgent === 0) {
        return `Rådgivare i totalt ${numTotalIssues} emissioner som finansiell rådgivare (${numAsFinancialAdvisor}) och som juridisk rådgivare (${numAsLegalAdvisor}).`
    }
    if (numAsFinancialAdvisor > 0 && numAsLegalAdvisor === 0 && numAsIssuingAgent > 0) {
        return `Rådgivare i totalt ${numTotalIssues} emissioner som finansiell rådgivare (${numAsFinancialAdvisor}) och som emissionsinstitut (${numAsIssuingAgent}).`
    }
    if (numAsFinancialAdvisor === 0 && numAsLegalAdvisor > 0 && numAsIssuingAgent > 0) {
        return `Rådgivare i totalt ${numTotalIssues} emissioner som juridisk rådgivare (${numAsLegalAdvisor}) och som emissionsinstitut (${numAsIssuingAgent}).`
    }
    if (numAsFinancialAdvisor > 0 && numAsLegalAdvisor > 0 && numAsIssuingAgent > 0) {
        return `Rådgivare i totalt ${numTotalIssues} emissioner som finansiell rådgivare (${numAsFinancialAdvisor}), juridisk rådgivare (${numAsLegalAdvisor}) och emissionsinstitut (${numAsIssuingAgent}).`
    }
    return 'Rådgivare i emissioner'
}

const advisorTypeLabels = (advisors) => {
    const labels = advisors.map((a) => {
        if (a === 'financial_advisor') {
            return 'Finansiell rådgivare'
        }
        if (a === 'legal_advisor') {
            return 'Juridisk rådgivare'
        }
        if (a === 'issuing_agent') {
            return 'Emissionsinstitut'
        }
        return null
    })
    return labels.join(', ')
}

function IssuesDetails({ entity }) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const [issues, setIssues] = React.useState(null)
    const issueApi = new IssueApi()
    const { isAuthenticated } = useAuth()
    useEffect(() => {
        if (!isAuthenticated || !entity) return
        const fetchData = async () => {
            try {
                const result = await issueApi.findByAdvisorId(entity.id)
                setIssues(result)
            } catch (e) {
                console.error(e)
            }
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, entity])
    let content = null
    if (issues && issues.length > 0) {
        // normalize the issues into an object where the key is the issue id, the "data" is the issue
        // and the "advisors" is an array of advisor types
        let normalizedIssues = {}
        issues.forEach((i) => {
            if (!normalizedIssues[i.id]) {
                normalizedIssues[i.id] = { data: i, advisors: [] }
            }
            normalizedIssues[i.id].advisors.push(i.advisorType)
        })
        let sortedIssues = Object.values(normalizedIssues)
            .map((i) => {
                i.data.advisors = i.advisors
                return i.data
            })
            .sort((a, b) => {
                return new Date(b.subEndDate) - new Date(a.subEndDate)
            })

        let issuesListItems = null
        // loop through the sorted issues and find the max length of the advisors list
        const maxAdvisors = sortedIssues.reduce((max, issue) => {
            return issue.advisors.length > max ? issue.advisors.length : max
        }, 0)
        issuesListItems = sortedIssues.map((issue) => {
            return (
                <li key={issue.id}>
                    <Link component={NavLink} to={`/issues/${issue.id}`}>
                        {issue.shareName} - {formatDateYYYYMMDD(issue.subEndDate, locale)}
                    </Link>
                    {maxAdvisors > 1 && ` (${advisorTypeLabels(issue.advisors)})`}
                </li>
            )
        })
        content = (
            <React.Fragment>
                <Typography variant='body2' gutterBottom>
                    {advisorSummaryText(normalizedIssues)}
                </Typography>
                <ul>{issuesListItems}</ul>
            </React.Fragment>
        )
    }
    return (
        <InfoBlock title='Emissioner som rådgivare' loadingElement={!content}>
            {content}
        </InfoBlock>
    )
}

function AdvisorDetails() {
    const { advisorId } = useParams()
    const { t } = useTranslation()
    const [entity, setEntity] = React.useState(null)
    const { isAuthenticated } = useAuth()
    const advisorApi = new AdvisorApi()
    useEffect(() => {
        if (!isAuthenticated) return
        const fetchData = async () => {
            try {
                const result = await advisorApi.findByAdvisorId(advisorId)
                setEntity(result)
            } catch (e) {
                console.error(e)
            }
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated])
    return (
        <React.Fragment>
            <Helmet title={t('site.advisors.detailsTitle', { name: entity?.name })} />
            <Typography variant='h3' gutterBottom display='inline'>
                {t('site.advisors.detailsTitle', { name: entity?.name })}
            </Typography>

            <Divider my={6} />

            <Grid container spacing={6}>
                <Grid xs={12}>
                    {!entity && <LinearProgress />}
                    {entity && <DetailsView entity={entity} />}
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default AdvisorDetails
